import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Heart, Gift, AlertCircle, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { BonusCard } from '../components/BonusCard';
import { TestimonialCard } from '../components/TestimonialCard';
import { SocialProofPopup } from '../components/SocialProofPopup';
import { FAQ } from '../components/FAQ';
import { Flame, Clock, ShieldCheck, Zap, Layout, BookOpen, RefreshCw } from 'lucide-react';
export function LandingPage() {
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const [currentSlide, setCurrentSlide] = useState(0);
  const purchaseSectionRef = useRef<HTMLElement>(null);
  const [viewContentFired, setViewContentFired] = useState(false);
  const slides = ['https://cdn.magicpatterns.com/uploads/7BiU6DqDu6wBM5qYDY5Gxv/MAS.jpg', 'https://cdn.magicpatterns.com/uploads/2Gj4DAKysis3hg9wXEQWUv/PRINCESAS.jpg', 'https://cdn.magicpatterns.com/uploads/eRwzx9M7k6nF54KKJQKc51/BAUTISMO.jpg', 'https://cdn.magicpatterns.com/uploads/9VvzFVv48hwAEFmdQzo6r5/MIKEY.jpg'];
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  const scrollToPurchase = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handleCheckoutClick = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout');
    }
  };
  // ViewContent tracking when purchase section is visible
  useEffect(() => {
    if (!purchaseSectionRef.current) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !viewContentFired) {
          if (typeof window !== 'undefined' && (window as any).fbq) {
            ;
            (window as any).fbq('track', 'ViewContent');
            setViewContentFired(true);
          }
        }
      });
    }, {
      threshold: 0.5
    });
    observer.observe(purchaseSectionRef.current);
    return () => observer.disconnect();
  }, [viewContentFired]);
  return <div className="min-h-screen bg-gradient-to-b from-pink-50 via-purple-50 to-blue-50 font-sans text-gray-800 overflow-x-hidden" data-id="element-70">
      <SocialProofPopup data-id="element-71" />

      {/* Global Urgency Bar */}
      <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white py-2 text-center sticky top-0 z-50 shadow-lg overflow-x-hidden" data-id="element-72">
        <div className="px-2 max-w-full animate-pulse" data-id="element-73">
          <p className="text-xs md:text-sm font-bold" data-id="element-74">
            🔥 OFERTA ESPECIAL: termina en 24 horas — 60% OFF + Bonos Gratis 🔥
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <header className="relative pt-4 pb-5 px-4 md:pt-8 md:pb-10 overflow-x-hidden" data-id="element-75">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 opacity-20" data-id="element-76">
          <div className="absolute top-10 left-10 w-32 h-32 bg-pink-300 rounded-full blur-3xl" data-id="element-77"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-blue-300 rounded-full blur-3xl" data-id="element-78"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center" data-id="element-79">
          {/* New Main Title with Emojis */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-2xl md:text-4xl lg:text-5xl font-black text-gray-900 leading-tight mb-3 px-2" data-id="element-80">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500" data-id="element-81">
              ¡Transformá Globos en Oro! Ganá $100.000+ por Evento en Horas
              ✨🎈💰
            </span>
          </motion.h1>

          {/* New Subtitle */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-sm md:text-base text-gray-800 mb-3 max-w-3xl mx-auto font-semibold leading-snug px-2" data-id="element-82">
            Dejá de improvisar, perder clientes y cobrar eventos baratos:
            aprendé a crear decoraciones mágicas con globos, candy bars y
            temáticas personalizadas que cobrás entre $100.000 y $500.000 por
            evento — sin experiencia previa, desde tu primer cliente.
          </motion.p>

          {/* Social Proof Line */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="flex justify-center mb-3" data-id="element-83">
            <p className="text-xs md:text-sm text-gray-700 font-medium" data-id="element-84">
              ⭐ 4.9/5 calificación • 1.200+ decoradoras ya están ganando con
              este método • Resultados comprobados
            </p>
          </motion.div>

          {/* Mockup - 40% Larger, New Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4
        }} className="relative w-full max-w-[420px] mx-auto mb-3" data-id="element-85">
            <a href="#purchase-section" onClick={scrollToPurchase} className="block" data-id="element-86">
              <img src="https://cdn.magicpatterns.com/uploads/t24Nr7WeE7giJvTTRhF9Wh/image_(3).jpg" alt="Mega Pack Globología Mágica Pro" className="w-full" data-id="element-87" />
            </a>
          </motion.div>

          {/* Main CTA - Immediately Below Mockup */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="flex justify-center mb-4" data-id="element-88">
            <a href="#purchase-section" onClick={scrollToPurchase} className="inline-flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm md:text-base uppercase tracking-wide py-3 px-6 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 max-w-[90%]" data-id="element-89">
              ¡QUIERO EMPEZAR A GANAR $100.000+ POR EVENTO!
            </a>
          </motion.div>

          {/* New Text Below CTA */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="text-base md:text-lg text-gray-800 mb-5 max-w-3xl mx-auto leading-relaxed px-4 font-semibold" data-id="element-90">
            ¿Harta de eventos baratos y regateos? Dominá decoraciones épicas con
            globos y candy bars y empezá a cobrar $100.000–$500.000+ por evento,
            incluso desde cero. Más de 1.200 mujeres ya lo lograron. ¿Vos cuándo
            arrancás?
          </motion.p>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-10 px-4 bg-white relative overflow-x-hidden" data-id="element-91">
        <div className="max-w-4xl mx-auto" data-id="element-92">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center" data-id="element-93">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-5" data-id="element-94">
              ¿Te piden un evento… y lo primero que sentís es{' '}
              <span className="text-pink-600" data-id="element-95">miedo</span>?
            </h2>

            <div className="bg-red-50 p-5 rounded-2xl border border-red-100 mb-6 text-left md:text-center" data-id="element-96">
              <p className="font-medium text-sm text-gray-700 mb-2" data-id="element-97">
                Miedo a comprar mal, a combinar colores que no funcionen, a
                tardar demasiado o a que no quede prolijo.
              </p>
              <p className="font-bold text-base text-red-800" data-id="element-98">
                Y sobre todo, miedo a decir un precio… y sentir que estás
                improvisando.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 items-center mb-6 text-left" data-id="element-99">
              <div data-id="element-100">
                <h3 className="text-base font-bold text-purple-900 mb-2" data-id="element-101">
                  La diferencia entre improvisar y ser profesional
                </h3>
                <p className="text-gray-600 text-xs mb-2" data-id="element-102">
                  Hay decoradoras que trabajan tranquilas, seguras y cobran sin
                  dudar. No tienen más talento que vos, simplemente{' '}
                  <strong data-id="element-103">tienen un método.</strong>
                </p>
                <p className="text-gray-600 text-xs" data-id="element-104">
                  Sin un sistema, cada evento es una apuesta. Con el Mega Pack,
                  tenés el paso a paso para dejar de dudar.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100" data-id="element-105">
                <ul className="space-y-2" data-id="element-106">
                  {['¿Qué estructura conviene usar?', '¿Cuántos globos comprar?', '¿Cómo combinar colores?', '¿Cómo cobrar sin culpa?'].map((item, i) => <li key={i} className="flex items-center gap-2 text-gray-700 text-xs font-medium" data-id="element-107">
                      <AlertCircle className="w-3 h-3 text-pink-500 flex-shrink-0" data-id="element-108" />
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>

            <a href="#purchase-section" onClick={scrollToPurchase} className="inline-flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wide py-2.5 px-5 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300" data-id="element-109">
              ¡RECLAMAR MI MEGA PACK AHORA!
            </a>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-10 px-4 bg-gradient-to-b from-purple-900 to-indigo-900 text-white overflow-x-hidden" data-id="element-110">
        <div className="max-w-4xl mx-auto text-center" data-id="element-111">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-112">
            <h2 className="text-xl md:text-2xl font-bold mb-2 text-white" data-id="element-113">
              El problema no son los globos, es la falta de un sistema.
            </h2>
            <p className="text-base text-purple-100 mb-5" data-id="element-114">
              Acá tenés el paso a paso para armar, ofrecer y cobrar eventos
              completos sin dudas.
            </p>

            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-5 md:p-6 border border-white/20 shadow-2xl mt-5" data-id="element-115">
              <div className="flex justify-center mb-3" data-id="element-116">
                <div className="bg-pink-500 text-white font-bold px-4 py-1 rounded-full text-xs shadow-lg" data-id="element-117">
                  LA SOLUCIÓN DEFINITIVA
                </div>
              </div>

              <h3 className="text-2xl md:text-3xl font-extrabold mb-3 text-white drop-shadow-lg" data-id="element-118">
                Mega Pack Globología Mágica Pro
              </h3>
              <p className="text-sm mb-5 text-purple-100 max-w-2xl mx-auto" data-id="element-119">
                Un pack digital completo donde aprendés a convertir la
                globología y la decoración de eventos en un servicio rentable,
                ordenado y profesional.
              </p>

              <a href="#purchase-section" onClick={scrollToPurchase} className="inline-flex items-center justify-center bg-white text-purple-900 font-bold text-sm uppercase tracking-wide py-2.5 px-5 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300" data-id="element-120">
                ¡RECLAMAR MI MEGA PACK AHORA!
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo Slider */}
      <section className="py-12 px-4 bg-white overflow-x-hidden" data-id="element-121">
        <div className="max-w-5xl mx-auto" data-id="element-122">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-8" data-id="element-123">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" data-id="element-124">
              Este es el tipo de eventos que vas a poder armar con el método.
            </h2>
            <p className="text-gray-600 text-xs" data-id="element-125">
              Resultados reales y alcanzables para quienes están aprendiendo
            </p>
          </motion.div>

          <div className="relative max-w-3xl mx-auto mb-8" data-id="element-126">
            <div className="overflow-hidden rounded-2xl" data-id="element-127">
              <motion.img key={currentSlide} src={slides[currentSlide]} alt={`Evento ${currentSlide + 1}`} className="w-full h-auto" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5
            }} data-id="element-128" />
            </div>

            <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all" data-id="element-129">
              <ChevronLeft className="w-5 h-5 text-gray-800" data-id="element-130" />
            </button>
            <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all" data-id="element-131">
              <ChevronRight className="w-5 h-5 text-gray-800" data-id="element-132" />
            </button>

            <div className="flex justify-center gap-2 mt-4" data-id="element-133">
              {slides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? 'bg-purple-600 w-6' : 'bg-gray-300'}`} data-id="element-134" />)}
            </div>
          </div>

          <div className="flex justify-center" data-id="element-135">
            <a href="#purchase-section" onClick={scrollToPurchase} className="inline-flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wide py-2.5 px-5 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300" data-id="element-136">
              ¡RECLAMAR MI MEGA PACK AHORA!
            </a>
          </div>

          {/* What You Can Do */}
          <div className="mt-12 max-w-5xl mx-auto" data-id="element-137">
            <h3 className="text-xl md:text-2xl font-bold text-center text-gray-900 mb-6" data-id="element-138">
              Con el Mega Pack Globología Mágica vas a poder:
            </h3>
            <div className="grid md:grid-cols-2 gap-3" data-id="element-139">
              {[{
              text: 'Hacer arcos perfectos sin que se desarmen',
              icon: '🎈'
            }, {
              text: 'Crear figuras infantiles y animales',
              icon: '🦄'
            }, {
              text: 'Armar columnas, flores, personajes y estructuras complejas',
              icon: '🏗️'
            }, {
              text: 'Diseñar mesas de Candy Bar completas',
              icon: '🍬'
            }, {
              text: 'Combinar colores de forma profesional',
              icon: '🎨'
            }, {
              text: 'Presupuestar eventos sin dudar',
              icon: '💰'
            }, {
              text: 'Entregar trabajos profesionales aunque seas principiante',
              icon: '🎓'
            }, {
              text: 'Preparar decoraciones rápidas para trabajos urgentes',
              icon: '⚡'
            }].map((item, i) => <div key={i} className="flex items-start gap-2 bg-purple-50 p-3 rounded-xl border border-purple-100" data-id="element-140">
                  <span className="text-lg" data-id="element-141">{item.icon}</span>
                  <span className="text-gray-800 font-medium text-xs" data-id="element-142">
                    {item.text}
                  </span>
                </div>)}
            </div>
          </div>

          {/* What's Included */}
          <div className="mt-12 max-w-6xl mx-auto bg-white rounded-3xl p-5 md:p-8 border-2 border-purple-100 shadow-xl" data-id="element-143">
            <h3 className="text-xl md:text-2xl font-bold text-center text-gray-900 mb-3" data-id="element-144">
              Qué incluye exactamente el Mega Pack Globología Mágica Pro
            </h3>
            <p className="text-center text-sm text-purple-600 font-semibold mb-6" data-id="element-145">
              Cobrando $100.000–$500.000 por evento con decoraciones
              profesionales, incluso si empezás desde cero.
            </p>

            {/* Top Row - 3 Large Items */}
            <div className="grid md:grid-cols-3 gap-4 mb-5" data-id="element-146">
              {[{
              icon: <Zap className="w-8 h-8 text-purple-500" data-id="element-147" />,
              title: 'Curso completo de Decoración de Eventos',
              desc: 'Videos + PDF paso a paso'
            }, {
              icon: <Layout className="w-8 h-8 text-blue-500" data-id="element-148" />,
              title: 'Curso completo de Globología paso a paso',
              desc: 'Desde cero, técnicas claras y aplicables'
            }, {
              icon: <Star className="w-8 h-8 text-yellow-500" data-id="element-149" />,
              title: 'Curso completo de Mesas Dulces & Candy Bar',
              desc: 'Diseño y armado profesional'
            }].map((item, i) => <div key={i} className="flex flex-col items-center text-center gap-2 p-4 rounded-xl bg-purple-50 border border-purple-100 h-full" data-id="element-150">
                  <div className="bg-white p-2 rounded-full shadow-sm" data-id="element-151">
                    {item.icon}
                  </div>
                  <div data-id="element-152">
                    <h4 className="font-bold text-sm text-gray-900 leading-tight mb-1" data-id="element-153">
                      {item.title}
                    </h4>
                    <p className="text-[10px] text-gray-600" data-id="element-154">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            {/* Bottom Row - 4 Medium Items */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-155">
              {[{
              icon: <ShieldCheck className="w-6 h-6 text-green-500" data-id="element-156" />,
              title: 'Negocio rentable',
              desc: 'Cómo presupuestar y cobrar eventos'
            }, {
              icon: <Gift className="w-6 h-6 text-orange-500" data-id="element-157" />,
              title: 'Figuras y personajes',
              desc: 'Animales, flores y muñecos'
            }, {
              icon: <BookOpen className="w-6 h-6 text-pink-500" data-id="element-158" />,
              title: 'Organización',
              desc: 'Guías para gestionar eventos sin estrés'
            }, {
              icon: <Check className="w-6 h-6 text-indigo-500" data-id="element-159" />,
              title: 'Plantillas y personajes editables',
              desc: 'Editables en PowerPoint y Canva'
            }].map((item, i) => <div key={i} className="flex flex-col items-center text-center gap-1.5 p-2 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100 h-full" data-id="element-160">
                  <div className="bg-gray-100 p-1.5 rounded-full" data-id="element-161">
                    {item.icon}
                  </div>
                  <div data-id="element-162">
                    <h4 className="font-bold text-[10px] md:text-xs text-gray-900 leading-tight" data-id="element-163">
                      {item.title}
                    </h4>
                    <p className="text-[9px] text-gray-600 mt-0.5" data-id="element-164">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="mt-8 flex justify-center" data-id="element-165">
            <a href="#purchase-section" onClick={scrollToPurchase} className="inline-flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wide py-2.5 px-5 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300" data-id="element-166">
              ¡RECLAMAR MI MEGA PACK AHORA!
            </a>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-12 px-4 bg-pink-50 overflow-x-hidden" data-id="element-167">
        <div className="max-w-4xl mx-auto" data-id="element-168">
          <h2 className="text-xl md:text-2xl font-bold text-center text-gray-900 mb-3" data-id="element-169">
            Esto es para vos si…
          </h2>
          <p className="text-center text-sm text-purple-600 font-semibold mb-6" data-id="element-170">
            Cobrando $100.000–$500.000 por evento con decoraciones
            profesionales, incluso si empezás desde cero.
          </p>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-171">
            {['Querés empezar desde cero, pero no querés perder tiempo improvisando.', 'Ya decorás eventos, pero no sabés cuánto cobrar.', 'Sentís que te falta orden y seguridad.', 'Querés generar ingresos reales con decoración.', 'Estás cansada de trabajar mucho y ganar poco.', 'Querés sentirte profesional, aunque hoy te sientas amateur.'].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-4 rounded-xl shadow-sm border border-pink-100 flex items-center gap-3" data-id="element-172">
                <div className="bg-pink-100 p-1.5 rounded-full" data-id="element-173">
                  <Heart className="w-3 h-3 text-pink-500" data-id="element-174" />
                </div>
                <p className="font-medium text-gray-700 text-xs" data-id="element-175">{item}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-purple-50 relative overflow-hidden" data-id="element-176">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl" data-id="element-177">
          <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white py-1.5 px-6 text-center font-bold text-[10px] md:text-xs tracking-wide shadow-lg animate-pulse" data-id="element-178">
            🎁 OFERTA ESPECIAL POR TIEMPO LIMITADO
          </div>
        </div>

        <div className="max-w-6xl mx-auto pt-8" data-id="element-179">
          <div className="text-center mb-10" data-id="element-180">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-181">
              Regalos Exclusivos
            </h2>
            <p className="text-base text-purple-600 font-medium" data-id="element-182">
              Gratis con tu compra hoy
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-6" data-id="element-183">
            <BonusCard image="https://cdn.magicpatterns.com/uploads/uq8Ym4Ws7WM389REmdubso/Bono_1.png" title="Bono 1: Curso de Diseño Gráfico" description="Aprendé diseño gráfico aplicado al Candy Bar para crear decoraciones profesionales y atractivas. ($97.000 VALOR REAL)" delay={0.1} data-id="element-184" />
            <BonusCard image="https://cdn.magicpatterns.com/uploads/15zSeDofzVaY1MudHG297e/Bono_2.png" title="Bono 2: Pack de Instalación Candy Bar" description="Instalador de Corel Draw 2025 y PowerPoint para que empieces a diseñar desde el primer día. ($150.000 VALOR REAL)" delay={0.2} data-id="element-185" />
            <BonusCard image="https://cdn.magicpatterns.com/uploads/fowpgJm3akp3d4QM6rtD6Z/Bono_3.png" title="Bono 3: 500 Personajes Premium" description="500 personajes editables para Candy Bar, listos para usar en Canva y PowerPoint. ($200.000 VALOR REAL)" delay={0.3} data-id="element-186" />
          </div>

          <div className="text-center mb-8" data-id="element-187">
            <p className="text-lg md:text-xl font-bold text-purple-900" data-id="element-188">
              Valor total en bonos: $500.000+ — incluidos GRATIS hoy
            </p>
          </div>

          <div className="max-w-4xl mx-auto flex justify-center" data-id="element-189">
            <a href="#purchase-section" onClick={scrollToPurchase} className="inline-flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wide py-2.5 px-5 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300" data-id="element-190">
              ¡RECLAMAR MI MEGA PACK AHORA!
            </a>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-12 px-4 bg-gradient-to-b from-green-50 to-white overflow-x-hidden" data-id="element-191">
        <div className="max-w-3xl mx-auto" data-id="element-192">
          <div className="bg-white rounded-3xl p-5 md:p-8 shadow-xl border-2 border-green-200" data-id="element-193">
            <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left" data-id="element-194">
              <div className="bg-green-100 p-4 rounded-full flex-shrink-0" data-id="element-195">
                <ShieldCheck className="w-10 h-10 text-green-600" data-id="element-196" />
              </div>
              <div className="flex-grow" data-id="element-197">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2" data-id="element-198">
                  🛡 Garantía de Satisfacción Sin Preguntas – 7 Días
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-199">
                  Si en los próximos 7 días sentís que el pack no es para vos,
                  te devolvemos tu dinero sin preguntas. Queremos que estés 100%
                  segura de tu decisión.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PURCHASE SECTION */}
      <section ref={purchaseSectionRef} id="purchase-section" className="py-12 px-4 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white relative overflow-hidden scroll-mt-20" data-id="element-200">
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-201">
          <h2 className="text-xl md:text-3xl font-bold mb-6" data-id="element-202">
            ¿Lista para dejar de improvisar?
          </h2>

          <div className="bg-white text-gray-900 rounded-3xl p-5 md:p-8 shadow-2xl max-w-lg mx-auto" data-id="element-203">
            {/* Header */}
            <div className="mb-4" data-id="element-204">
              <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-205">
                ACCESO COMPLETO DE POR VIDA
              </h3>
              <p className="text-sm text-gray-600 mb-3" data-id="element-206">
                Acceso inmediato a Mega Pack Globología Mágica Pro + Bonos
                ($500.000+ en valor). Empezá hoy y conseguí tu primer evento
                pago en semanas.
              </p>
              <div className="inline-block bg-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full" data-id="element-207">
                MEJOR OFERTA DISPONIBLE
              </div>
            </div>

            {/* Pills Above Price */}
            <div className="flex flex-wrap justify-center gap-1.5 mb-5" data-id="element-208">
              {['🎈 Principiantes', '🎉 Intermedias', '💰 Emprendedoras', '🛍 Decoradoras en crecimiento'].map((tag, i) => <span key={i} className="bg-purple-100 text-purple-700 text-[9px] font-bold px-2 py-0.5 rounded-full border border-purple-200" data-id="element-209">
                  {tag}
                </span>)}
            </div>

            <h3 className="text-lg md:text-xl font-bold mb-5" data-id="element-210">
              Mega Pack Globología Mágica Pro + 3 Bonos
            </h3>

            {/* What You Get Today List */}
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 mb-5 text-left" data-id="element-211">
              <h4 className="font-bold text-sm text-green-800 mb-3 text-center" data-id="element-212">
                LO QUE RECIBÍS HOY:
              </h4>
              <ul className="space-y-1.5 text-xs text-gray-700" data-id="element-213">
                {['Curso completo de globología paso a paso', 'Arcos, columnas y guirnaldas (técnica + armado)', 'Figuras: animales, flores y personajes', 'Candy Bar: diseño + armado profesional', 'Cómo presupuestar y cobrar sin dudar', 'Plantillas editables (Canva + PowerPoint)', 'Actualizaciones gratis', '3 bonos incluidos ($500.000+ valor)'].map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-214">
                    <Check className="w-3 h-3 text-green-600 flex-shrink-0" data-id="element-215" />
                    <span data-id="element-216">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Price Display */}
            <div className="flex flex-col items-center justify-center mb-4" data-id="element-217">
              <div className="flex items-center gap-2 mb-1" data-id="element-218">
                <span className="text-gray-400 line-through text-base" data-id="element-219">
                  $49.990
                </span>
                <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-220">
                  60% OFF
                </span>
              </div>
              <div className="flex items-start" data-id="element-221">
                <span className="text-xl font-bold mt-1" data-id="element-222">$</span>
                <span className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600" data-id="element-223">
                  19.990
                </span>
                <span className="text-base font-bold mt-5 ml-1 text-gray-500" data-id="element-224">
                  ARS
                </span>
              </div>
            </div>

            {/* Pills Below Price */}
            <div className="flex flex-wrap justify-center gap-1.5 mb-5 text-[9px] font-bold text-purple-700" data-id="element-225">
              <span className="flex items-center gap-0.5 bg-purple-50 px-2 py-0.5 rounded-md" data-id="element-226">
                ⭐ Acceso de por vida
              </span>
              <span className="flex items-center gap-0.5 bg-purple-50 px-2 py-0.5 rounded-md" data-id="element-227">
                ♻️ Actualizaciones gratis
              </span>
              <span className="flex items-center gap-0.5 bg-purple-50 px-2 py-0.5 rounded-md" data-id="element-228">
                🎁 Incluye 3 bonos
              </span>
            </div>

            {/* CTA to Checkout */}
            <a href="http://checkout.pixastore.online/cart/42406778339422:1" target="_blank" rel="noopener noreferrer" onClick={handleCheckoutClick} className="block w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-base md:text-lg uppercase tracking-wide py-3 px-6 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 mb-3 text-center animate-pulse" data-id="element-229">
              ¡QUIERO EMPEZAR A GANAR $100.000+ POR EVENTO!
            </a>

            {/* Guarantee */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3" data-id="element-230">
              <p className="text-xs text-green-800 font-bold text-center" data-id="element-231">
                Garantía total 7 días: probalo sin riesgos. Si no ves
                resultados, te devolvemos el 100%.
              </p>
            </div>

            <p className="text-xs text-gray-500 font-medium mb-5" data-id="element-232">
              Acceso inmediato + Garantía total de 7 días
            </p>

            {/* Mercado Pago Logo */}
            <div className="mt-5 flex flex-col items-center gap-2" data-id="element-233">
              <p className="text-xs font-bold text-gray-700" data-id="element-234">
                Pago 100% seguro procesado por Mercado Pago
              </p>
              <img src="https://cdn.magicpatterns.com/uploads/tkuqXfKJKrRMaPRAAtZJ3N/mercado-pago-logo-png_seeklogo-342347.png" alt="Mercado Pago" className="h-20 md:h-24 w-auto object-contain" data-id="element-235" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 px-4 bg-white overflow-x-hidden" data-id="element-236">
        <div className="max-w-4xl mx-auto mb-6" data-id="element-237">
          <p className="text-center text-sm text-purple-600 font-semibold" data-id="element-238">
            Cobrando $100.000–$500.000 por evento con decoraciones
            profesionales, incluso si empezás desde cero.
          </p>
        </div>
        <FAQ data-id="element-239" />
      </section>

      {/* Testimonials */}
      <section className="py-12 px-4 bg-gray-50 overflow-x-hidden" data-id="element-240">
        <div className="max-w-6xl mx-auto" data-id="element-241">
          <div className="text-center mb-3" data-id="element-242">
            <p className="text-base md:text-lg font-bold text-purple-900" data-id="element-243">
              💬 +1.200 alumnas ya están ganando $100.000+ por evento en
              Argentina
            </p>
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-center text-gray-900 mb-3" data-id="element-244">
            Ellas ya están facturando con sus eventos
          </h2>
          <div className="flex justify-center mb-8" data-id="element-245">
            <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-2 animate-pulse" data-id="element-246">
              👀 124 personas están viendo esta página ahora
            </span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-247">
            <TestimonialCard name="Mariana G." location="Salta, Argentina" text="Antes me daba pánico pasar un presupuesto. Con el método del pack, ahora sé exactamente cuánto cobrar. ¡Recuperé la inversión en mi primer evento!" tag="Más ingresos" delay={0.1} data-id="element-248" />
            <TestimonialCard name="Lucía R." location="Mendoza, Argentina" text="Los bonos son increíbles. No sabía nada de diseño y con el curso de regalo pude armar mis propias etiquetas. Mis mesas quedaron hermosas." tag="Eventos más prolijos" delay={0.2} data-id="element-249" />
            <TestimonialCard name="Carolina M." location="Rosario, Santa Fe" text="Lo que más me sirvió fue la parte de organización. Vivía corriendo a último momento. Ahora tengo todo planificado." tag="Más seguridad" delay={0.3} data-id="element-250" />
            <TestimonialCard name="Valentina S." location="CABA, Buenos Aires" text="Empecé sin saber inflar un globo. Las explicaciones son súper claras. Ya hice 3 cumpleaños y todos quedaron encantados." tag="Ideal principiantes" delay={0.4} data-id="element-251" />
            <TestimonialCard name="Sofía L." location="La Plata, Buenos Aires" text="Llevaba años decorando pero sentía que mis trabajos se veían 'caseros'. Con las técnicas de estructuras prolijas, mis arcos ahora parecen de revista." tag="Más clientes" delay={0.5} data-id="element-252" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 px-4 bg-white text-center overflow-x-hidden" data-id="element-253">
        <div className="max-w-3xl mx-auto" data-id="element-254">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3" data-id="element-255">
            No es magia. Es un sistema.
          </h2>
          <p className="text-base text-gray-600 mb-5" data-id="element-256">
            Mega Pack Globología Mágica Pro no promete resultados irreales.
            Promete algo mejor: orden, claridad y un método que se puede
            repetir.
          </p>
          <p className="text-sm font-bold text-purple-700 mb-6" data-id="element-257">
            Y los sistemas se repiten. Se cobran. Y se pueden hacer crecer.
          </p>

          <div className="flex flex-col items-center gap-4" data-id="element-258">
            <a href="http://checkout.pixastore.online/cart/42406778339422:1" target="_blank" rel="noopener noreferrer" onClick={handleCheckoutClick} className="inline-flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-base uppercase tracking-wide py-3 px-8 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300" data-id="element-259">
              ¡RECLAMAR MI MEGA PACK AHORA!
            </a>
            <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-260">
              <Gift className="w-3 h-3" data-id="element-261" />
              <span data-id="element-262">Incluye los 3 bonos gratis hoy ($500.000+ valor)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs overflow-x-hidden" data-id="element-263">
        <p data-id="element-264">
          © 2024 Mega Pack Globología Mágica Pro. Todos los derechos
          reservados.
        </p>
        <p className="mt-2" data-id="element-265">
          Este producto no garantiza resultados económicos. El éxito depende de
          tu dedicación y esfuerzo.
        </p>
      </footer>
    </div>;
}